if (!customElements.get('select-size-panel')) {
  customElements.define(
    'select-size-panel',
    class SelectSizePanel extends HTMLElement {
      connectedCallback() {
        // overlay-component moves our container to document.body on init, which
        // triggers disconnect + reconnect and would double-subscribe us.
        if (this.initialized) return;
        this.initialized = true;

        this.sectionId = this.dataset.section;
        this.productId = this.dataset.productId;
        this.productFormId = this.dataset.formId;
        this.mode = this.dataset.mode || 'pdp';
        this.overlayId = `select-size-panel-${this.sectionId}`;
        this.lastState = this.dataset.initialState || 'unselected';
        this.homeView = this.dataset.homeView || 'sizes';

        this.addToBagButton = this.querySelector('[data-add-to-bag]');
        this.shopPayButton = this.querySelector('[data-shop-pay-defer]');
        this.errorEl = this.querySelector('[data-error]');
        this.defaultErrorText = this.errorEl?.textContent?.trim() || '';
        this.notifyForm = this.querySelector('[data-notify-form]');
        this.notifyContinueButton = this.querySelector('[data-notify-continue]');
        this.addToBagButton?.addEventListener('click', this.handleSubmitClick.bind(this));
        // Capture-phase so a no-size click is intercepted before shop-js's own handler.
        this.shopPayButton?.addEventListener('click', this.handleShopPayClick.bind(this), true);
        this.notifyForm?.addEventListener('submit', this.handleNotifySubmit.bind(this));
        this.notifyContinueButton?.addEventListener('click', () => {
          document.getElementById(this.overlayId)?.close?.();
        });

        // Intra-panel view switcher (sizes <-> size-guide). Listen on the
        // overlay content since the back chevron lives in the header, which is
        // a sibling of this custom element.
        this.closest('[data-overlay-content]')?.addEventListener('click', this.handleViewTrigger.bind(this));

        // PLP: tile click selects the size; the footer Add to bag commits.
        this.querySelectorAll('[data-tile]').forEach((tile) => {
          tile.addEventListener('keydown', this.handleTileKeydown.bind(this));
          if (this.mode === 'pdp') tile.addEventListener('click', this.handlePdpTileClick.bind(this));
        });
        this.syncAriaChecked();
        if (this.mode === 'plp') {
          this.querySelectorAll('[data-tile]').forEach((tile) => {
            tile.addEventListener('click', this.handlePlpTileClick.bind(this));
          });
        }

        this.optionChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionChange.bind(this)
        );
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
        // Close the panel on cart add so the bag overlay isn't stacked behind it.
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate.bind(this));
        // PDP: surface /cart/add failures (e.g. max quantity reached) in our
        // error slot — the form-level error wrapper is gone in size_panel_id mode.
        if (this.mode === 'pdp') {
          this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, this.handleCartError.bind(this));
        }

        // <product-info> is fully swapped when variant changes on multi-option
        // products (color + size), which drops the hero ATC's overlay-trigger
        // listener. Delegate the open from document so a fresh trigger still works.
        document.addEventListener('click', this.handleTriggerClick.bind(this));

        // Reset to the home view and the notify form state whenever the
        // overlay closes so the next open doesn't land on a sub-view or the
        // success screen. Home view is 'sizes' by default, or 'notify' for
        // single-size sold-out PDPs where the panel renders notify-only.
        const overlay = document.getElementById(this.overlayId);
        overlay?.addEventListener('overlay-component:close', () => {
          this.showView(this.homeView);
          this.setNotifyState('form');
          this.notifyForm?.reset();
          this.hideError();
        });

        // Reflect an SSR-preselected size (e.g. ?variant=) on mount.
        this.updateShopPay();
      }

      handleViewTrigger(event) {
        const trigger = event.target.closest('[data-view-trigger]');
        if (!trigger) return;
        event.preventDefault();
        this.showView(trigger.dataset.viewTrigger);
      }

      showView(viewName) {
        this.dataset.currentView = viewName;
        this.querySelectorAll('[data-view]').forEach((view) => {
          view.toggleAttribute('hidden', view.dataset.view !== viewName);
        });

        // The header is a sibling of <select-size-panel> inside
        // [data-overlay-content]. overlay-component moves this whole subtree to
        // document.body, so go up from `this` rather than from the overlay id.
        const overlayContent = this.closest('[data-overlay-content]');
        const titleEl = overlayContent?.querySelector('[data-panel-title]');
        const backEl = overlayContent?.querySelector('.select-size-panel__header-back');
        const isSubView = viewName !== this.homeView;
        if (titleEl) titleEl.toggleAttribute('hidden', isSubView);
        if (backEl) backEl.toggleAttribute('hidden', !isSubView);
      }

      handleTriggerClick(event) {
        const trigger = event.target.closest(
          `.overlay-trigger[data-target="${this.overlayId}"], .card__quick-add-trigger[data-target="${this.overlayId}"]`
        );
        if (!trigger) return;
        const overlay = document.getElementById(this.overlayId);
        // PDP keeps the hash (for #select-size deep-links); PLP doesn't.
        overlay?.open?.(this.mode !== 'plp');
      }

      handlePlpTileClick(event) {
        // PLP has no variant-selects → no optionValueSelectionChange pubsub.
        // Mirror handleOptionChange's selection logic here on a direct click.
        event.preventDefault();
        const tile = event.currentTarget;
        if (!tile.dataset.variantId) return;
        this.hideError();
        this.querySelectorAll('[data-tile][data-selected]').forEach((t) => t.removeAttribute('data-selected'));
        tile.setAttribute('data-selected', 'true');
        this.lastState = tile.dataset.state || 'in_stock';
        this.applyCtaState(this.lastState);
        this.syncAriaChecked();
      }

      // Tiles are role="radio" buttons, not labels. On the PDP, check the form's
      // hidden radio and fire change so <variant-selects> reacts exactly as it
      // did to a label click.
      handlePdpTileClick(event) {
        const input = document.getElementById(event.currentTarget.dataset.inputId);
        if (!input || input.checked) return;
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Arrow keys move and select, like a native radio group.
      handleTileKeydown(event) {
        const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
        if (!step) return;
        event.preventDefault();
        const tiles = [...this.querySelectorAll('[data-tile]')];
        const next = tiles[(tiles.indexOf(event.currentTarget) + step + tiles.length) % tiles.length];
        next?.focus();
        next?.click();
      }

      syncAriaChecked() {
        this.querySelectorAll('[data-tile]').forEach((tile) =>
          tile.setAttribute('aria-checked', String(tile.hasAttribute('data-selected')))
        );
      }

      handleOptionChange({ data }) {
        const changedValue = data?.target?.value;
        if (!changedValue) return;
        const tile = this.querySelector(`[data-tile][data-option-value="${CSS.escape(changedValue)}"]`);
        if (!tile) return;

        this.hideError();

        // Mark this tile as the selected one (variantChange may not fire for
        // OOS combos that Shopify can't resolve).
        this.querySelectorAll('[data-tile][data-selected]').forEach((t) => t.removeAttribute('data-selected'));
        tile.setAttribute('data-selected', 'true');

        this.lastState = tile.dataset.state || 'in_stock';
        this.applyCtaState(this.lastState);
        this.syncAriaChecked();
      }

      handleSubmitClick(event) {
        const selectedTile = this.querySelector('[data-tile][data-selected]');
        if (!selectedTile) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.showError();
          return;
        }
        this.setAddToBagLoading(true);
        // PDP's button submits via form="..." natively. PLP has no form, so
        // route the click into our fetch instead.
        if (this.mode === 'plp') {
          event.preventDefault();
          this.addToCartFromPlp(selectedTile.dataset.variantId);
        }
      }

      // No size selected: block the checkout and show the select-a-size error.
      handleShopPayClick(event) {
        if (this.querySelector('[data-tile][data-selected]')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showError();
      }

      setAddToBagLoading(loading) {
        if (!this.addToBagButton) return;
        this.addToBagButton.classList.toggle('loading', loading);
        const spinner = this.addToBagButton.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !loading);
      }

      markSelectedTile(variant) {
        if (!variant) return;
        const values = [variant.option1, variant.option2, variant.option3].filter(Boolean);
        this.querySelectorAll('[data-tile]').forEach((tile) => {
          const match = values.includes(tile.dataset.optionValue);
          tile.toggleAttribute('data-selected', match);
        });
        this.syncAriaChecked();
      }

      showError(message) {
        if (this.errorEl) {
          if (message) this.errorEl.textContent = message;
          this.errorEl.hidden = false;
        }
        this.applyCtaState(this.lastState || 'unselected');
      }

      hideError() {
        if (this.errorEl) {
          this.errorEl.hidden = true;
          if (this.defaultErrorText) this.errorEl.textContent = this.defaultErrorText;
        }
        this.applyCtaState(this.lastState || 'unselected');
      }

      handleCartError(payload) {
        if (payload?.source !== 'product-form') return;
        this.setAddToBagLoading(false);
        const message = typeof payload.errors === 'string' ? payload.errors : payload.message;
        if (message) this.showError(message);
      }

      handleVariantChange({ data }) {
        if (!data || data.sectionId !== this.sectionId) return;
        // Re-sync the selected tile; don't reclassify — variantChange's payload
        // omits inventory_quantity so we'd downgrade 'low' to 'in_stock'.
        this.markSelectedTile(data.variant);
      }

      handleCartUpdate(payload) {
        // product-form.js publishes this flat — not wrapped in { data } like variantChange.
        if (payload?.source !== 'product-form') return;
        this.setAddToBagLoading(false);
        document.getElementById(this.overlayId)?.close?.();
      }

      applyCtaState(state) {
        const stockInfoLow = this.querySelector('[data-stock-info-low]');
        const stockInfoOos = this.querySelector('[data-stock-info-oos]');
        const addToBag = this.querySelector('[data-add-to-bag]');
        const notifyMe = this.querySelector('[data-notify-me]');
        const errorVisible = this.errorEl && !this.errorEl.hidden;

        if (stockInfoLow) stockInfoLow.toggleAttribute('hidden', errorVisible || state !== 'low');
        if (stockInfoOos) stockInfoOos.toggleAttribute('hidden', errorVisible || state !== 'oos');
        if (addToBag) addToBag.toggleAttribute('hidden', state === 'oos');
        if (notifyMe) notifyMe.toggleAttribute('hidden', state !== 'oos');
        this.updateShopPay();
      }

      // Shop Pay derives its enabled state from the Add to bag button so the
      // two never diverge. It stays visible in the panel at all times —
      // disabled until Add to bag is actionable AND a size is selected — so the
      // footer row keeps its two-column layout in every state, with no shift
      // when OOS swaps Add to bag for Notify me. Add to bag stays the single
      // source of truth, so future changes to its gating flow through for free.
      updateShopPay() {
        if (!this.shopPayButton || !this.addToBagButton) return;
        const variantId = this.querySelector('[data-tile][data-selected]')?.dataset.variantId;
        if (variantId) this.shopPayButton.setAttribute('variants', `${variantId}:1`);
        else this.shopPayButton.removeAttribute('variants');
        // Mirror Add to bag's actionable state, not the selection: clickable while
        // unselected, inert only on OOS. shop-js has no disabled API; CSS dims off [inert].
        this.shopPayButton.toggleAttribute('inert', !this.isAddToBagActionable());
      }

      // Add to bag is actionable when it's shown (not OOS) and not disabled.
      // Reading disabled/aria-disabled too means any future loading/guard state
      // added to Add to bag disables Shop Pay along with it.
      isAddToBagActionable() {
        const btn = this.addToBagButton;
        return !!btn && !btn.hidden && !btn.disabled && btn.getAttribute('aria-disabled') !== 'true';
      }

      handleNotifySubmit(event) {
        event.preventDefault();

        const form = event.currentTarget;
        const email = form.querySelector('input[name="email"]')?.value?.trim();
        const acceptsMarketing = !!form.querySelector('input[name="accepts_marketing"]')?.checked;
        const variantId = this.getCurrentVariantId();

        if (!email || !variantId) return;

        // Back in Stock (useamp.com) injects `_BIS` with a create() method.
        // See https://help.useamp.com/article/757-javascript-api
        // _BIS.create is fire-and-forget (no promise), so we optimistically
        // switch to the success state on any call. In dev the global isn't
        // present — we still show success so the UI is mockable end-to-end.
        const api = window._BIS || window.BIS;
        try {
          api?.create?.(email, variantId, this.productId, { accepts_marketing: acceptsMarketing });
        } catch (err) {
          console.error('[select-size-panel] BIS.create failed', err);
        }

        this.setNotifyState('success');
      }

      setNotifyState(state) {
        const isSuccess = state === 'success';
        this.querySelector('[data-notify-intro]')?.toggleAttribute('hidden', isSuccess);
        this.querySelector('[data-notify-thank-you]')?.toggleAttribute('hidden', !isSuccess);
        this.notifyForm?.toggleAttribute('hidden', isSuccess);
        this.querySelector('[data-notify-help]')?.toggleAttribute('hidden', isSuccess);
        this.querySelector('[data-notify-submit]')?.toggleAttribute('hidden', isSuccess);
        this.querySelector('[data-notify-recommendations]')?.toggleAttribute('hidden', !isSuccess);
        this.notifyContinueButton?.toggleAttribute('hidden', !isSuccess);
      }

      getCurrentVariantId() {
        // Selected tile is the source of truth in both modes; PDP form is the
        // fallback for cases where no tile selection has happened yet.
        const selectedTile = this.querySelector('[data-tile][data-selected]');
        if (selectedTile?.dataset.variantId) return selectedTile.dataset.variantId;
        if (!this.productFormId) return null;
        const form = document.getElementById(this.productFormId);
        return form?.querySelector('input[name="id"]')?.value || null;
      }

      async addToCartFromPlp(variantId) {
        if (this.cartAddInFlight) return;
        this.cartAddInFlight = true;

        // Skip cart.renderContents — cart-notification redirects to
        // #shop-the-look, which PLP doesn't have. Patch header sections here.
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const sectionsToRefresh = ['cart-icon-bubble', 'cart-notification-button'];
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        formData.append('sections', sectionsToRefresh.join(','));
        formData.append('sections_url', window.location.pathname);
        config.body = formData;

        try {
          const response = await fetch(`${routes.cart_add_url}`, config);
          const data = await response.json();
          if (data.status) {
            const message = data.description || data.message;
            if (message) this.showError(message);
            return;
          }
          this.patchSections(data.sections, sectionsToRefresh);
          this.populateAddedToBagOverlay(data);
          document.getElementById('plp-added-to-bag')?.open?.(false);
          // Closes the size panel via handleCartUpdate.
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: variantId,
            cartData: data,
          });
        } catch (err) {
          console.error('[select-size-panel] cart add error', err);
        } finally {
          this.cartAddInFlight = false;
          this.setAddToBagLoading(false);
        }
      }

      patchSections(sectionsHtml, ids) {
        if (!sectionsHtml) return;
        ids.forEach((id) => {
          const target = document.getElementById(id);
          const html = sectionsHtml[id];
          if (!target || !html) return;
          const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
          target.innerHTML = fresh ? fresh.innerHTML : html;
        });
      }

      populateAddedToBagOverlay(cartItem) {
        // overlay-component moves the container to body — query by attr.
        const container = document.querySelector('[data-overlay-container="plp-added-to-bag"]');
        if (!container) return;
        const imageEl = container.querySelector('[data-plp-added-image]');
        const titleEl = container.querySelector('[data-plp-added-title]');
        const priceEl = container.querySelector('[data-plp-added-price]');
        if (imageEl && cartItem.image) {
          imageEl.src = cartItem.image;
          imageEl.alt = cartItem.title || '';
        }
        if (titleEl) titleEl.textContent = cartItem.product_title || cartItem.title || '';
        if (priceEl && typeof cartItem.price === 'number') {
          // formatMoney throws without a money_format — fall back to cents/100.
          const cents = cartItem.price;
          const moneyFormat = window.Shopify?.money_format;
          let formatted = (cents / 100).toFixed(2);
          if (typeof formatMoney === 'function' && moneyFormat) {
            try {
              formatted = formatMoney(cents, moneyFormat);
            } catch {
              /* keep numeric fallback */
            }
          }
          priceEl.textContent = formatted;
        }

        // Repoint <product-recommendations> at the added product. Clear its
        // cache flags, otherwise only the first product ever loads.
        const rec = container.querySelector('product-recommendations[data-plp-recommendations]');
        if (rec && cartItem.product_id) {
          rec.dataset.productId = String(cartItem.product_id);
          rec.dataset.complete = 'false';
          rec.isAwaitingResponse = false;
          rec.innerHTML = '';
          if (typeof rec.loadRecommendations === 'function') {
            rec.loadRecommendations(cartItem.product_id);
          }
        }
      }
    }
  );
}
